import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
    Table, Button, Modal, Input, Dropdown, Menu, Select, Rate,
    message, Form, Tooltip, Upload, Image
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, CheckOutlined,
    SearchOutlined, BellOutlined,
    ReloadOutlined, CameraOutlined,
    StopOutlined, RollbackOutlined, LoginOutlined, CheckCircleOutlined, EllipsisOutlined,
    LeftOutlined, RightOutlined, ArrowLeftOutlined, DownOutlined, EditOutlined,
} from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { useAttendanceActions } from '_actions/attendance.actions';
import { useStudentInfoAction } from '_actions/studentInfo.actions';
import { studentsAtom } from '_state';
import { useClassWrapper } from '_helpers';
import { ROLE_GROUPS } from '_helpers/roles';
import { normalizeVietnamese } from '_helpers/normalize';
import { useSessionWeek } from '_helpers/useSessionWeek';
import { HOMEWORK_LEVELS, homeworkLabel } from '_helpers/homework';
import DatePicker from '_helpers/DatePicker';
import dayjs from '_helpers/datetime';

// ─── Status vocabulary ─────────────────────────────────────────────────────────
// '' = chưa điểm danh (unmarked); 'paused' = bảo lưu (auto for suspended students).
const STATUS_LABEL = { '': 'Chưa điểm danh', present: 'Có mặt', absent: 'Vắng', late: 'Muộn', excused: 'Có phép', paused: 'Bảo lưu' };
// §4.5 tag-tone mapping.
const STATUS_TONE = { '': null, present: 'ok', absent: 'bad', late: 'warn', excused: 'info', paused: 'neutral' };
// Statuses a teacher can pick from the dropdown (paused is automatic, not manually set here).
const STATUS_SELECT = ['present', 'absent', 'late', 'excused'];
// Homework level → §4.5 tone.
const LEVEL_TONE = { excellent: 'ok', complete: 'info', incomplete: 'warn', none: 'bad' };

// Read-only status tag — §4.5 tinted tag, or a plain "—" when unmarked.
function StatusTag({ status }) {
    const tone = STATUS_TONE[status];
    if (!tone) return <span className="att2-dash">—</span>;
    return <span className={`att2-tag t-${tone}`}>{STATUS_LABEL[status]}</span>;
}

// Hover-to-open status picker: hover the current tag → menu drops, click a status to set.
// No initial click needed. Suspended (paused) and cancelled sessions render read-only.
function StatusSelect({ status, disabled, onPick }) {
    if (status === 'paused') return <StatusTag status="paused" />;
    if (disabled) return <StatusTag status={status} />;
    const tone = STATUS_TONE[status];
    const menu = (
        <Menu className="att2-statmenu" selectedKeys={status ? [status] : []} onClick={({ key }) => onPick(key)}>
            {STATUS_SELECT.map(s => (
                <Menu.Item key={s}>
                    <span className={`att2-tag t-${STATUS_TONE[s]}`}>{STATUS_LABEL[s]}</span>
                </Menu.Item>
            ))}
        </Menu>
    );
    return (
        <Dropdown overlay={menu} trigger={['hover', 'click']} placement="bottomLeft">
            <span className={'att2-statsel' + (tone ? ` t-${tone}` : ' att2-statsel-empty')}>
                {tone ? STATUS_LABEL[status] : 'Chưa ĐD'}
                <DownOutlined className="att2-statsel-caret" />
            </span>
        </Dropdown>
    );
}

// Read-only homework tag for compact display (unused rows show "—").
function HomeworkTag({ code }) {
    if (!code) return <span className="att2-dash">—</span>;
    return <span className={`att2-tag t-${LEVEL_TONE[code] || 'neutral'}`}>{homeworkLabel(code)}</span>;
}

const byLastName = (a, b) => {
    const la = normalizeVietnamese((a.name || '').trim().split(' ').pop());
    const lb = normalizeVietnamese((b.name || '').trim().split(' ').pop());
    return la < lb ? -1 : la > lb ? 1 : 0;
};

// Avatar initials — last two words of a Vietnamese name.
const initials = (n) => (n || '?').trim().split(/\s+/).slice(-2).map(w => w[0]).join('').toUpperCase();

const dayLabel = (d) => ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][d.day()];

export { AttendanceTable };

// Show why a write was refused, not just that it was. The server already sends a usable reason
// (e.g. "Lớp đã lưu trữ — chỉ đọc"); RES_FORM.message is only a string on the error paths, so fall
// back rather than hand antd an object. One `key` for all of them so a burst of failures replaces
// itself instead of stacking one toast per click.
const patchFailed = (res) => {
    const why = typeof res?.message === 'string' ? res.message : null;
    message.error({ content: why || 'Cập nhật thất bại', key: 'att-patch-fail' });
};

function AttendanceTable({ classId: classIdProp } = {}) {
    const classWrapper = useClassWrapper();
    const attendanceActions = useAttendanceActions();
    const studentInfoAction = useStudentInfoAction();
    // Canonical class roster — same source as Thông tin học sinh / Bảng điểm.
    const classMembers = useRecoilValue(studentsAtom);
    const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || 'null'), []);
    const isTeacher = ROLE_GROUPS.STAFF.includes(userData?.role);

    const [sessions,       setSessions]       = useState([]);
    const [loading,        setLoading]        = useState(false);
    const [openSessionId,  setOpenSessionId]  = useState(null); // null = card list; else = session detail
    const [createVisible,  setCreateVisible]  = useState(false);
    const [createLoading,  setCreateLoading]  = useState(false);
    const [form] = Form.useForm();
    const [search, setSearch] = useState('');
    const [editInfoTarget, setEditInfoTarget] = useState(null); // sessionId being edited (lesson name/description)
    const [editLessonName, setEditLessonName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editInfoLoading, setEditInfoLoading] = useState(false);
    const [attendanceView, setAttendanceView] = useState('week');

    const classId = classIdProp ?? classWrapper.curClass?.class_id;

    // ─── Refs — stable callbacks always see the latest values ─────────────────
    const classIdRef           = useRef(classId);           classIdRef.current           = classId;
    const attendanceActionsRef = useRef(attendanceActions); attendanceActionsRef.current = attendanceActions;
    const studentInfoActionRef = useRef(studentInfoAction); studentInfoActionRef.current = studentInfoAction;

    // ─── Load ─────────────────────────────────────────────────────────────────
    const loadSessions = useCallback(async () => {
        if (!classIdRef.current) return;
        setLoading(true);
        try {
            const data = await attendanceActionsRef.current.getSessions(classIdRef.current);
            setSessions(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('loadSessions:', e);
            setSessions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setSessions([]);
        setOpenSessionId(null); // switching class returns to the list
        loadSessions();
    }, [loadSessions, classId]);

    // Week-based session window (shared with the manager overview + learning results).
    const {
        weekSessions, controls: weekControls, label: weekLabel, count: weekCount,
        prevWeek: weekPrev, nextWeek: weekNext, goToday: weekToday,
        hasPast: weekHasPast, hasFuture: weekHasFuture,
    } = useSessionWeek(sessions, classId);

    const todaySessions = useMemo(() => {
        const today = dayjs();
        return [...sessions]
            .filter(sess => sess.date && dayjs(sess.date).isSame(today, 'day'))
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [sessions]);

    const nearestTodayId = useMemo(() => {
        if (!todaySessions.length) return null;
        const nowMs = dayjs().valueOf();
        return todaySessions.reduce((best, sess) => {
            const diff = Math.abs(dayjs(sess.date).valueOf() - nowMs);
            return !best || diff < best.diff ? { id: sess._id, diff } : best;
        }, null)?.id;
    }, [todaySessions]);

    // Fallback: load roster if the atom is empty (direct navigation to Điểm danh).
    useEffect(() => {
        if (isTeacher && classWrapper.curClass && classMembers.length === 0) {
            studentInfoActionRef.current.getStudentList(classWrapper.curClass);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTeacher, classId]);

    // ─── Per-session record lookup: sessionId -> Map(studentMongoId -> record) ─
    const recordMaps = useMemo(() => {
        const m = new Map();
        sessions.forEach(sess => {
            const inner = new Map();
            sess.records.forEach(rec => { if (rec.student_ref?._id) inner.set(rec.student_ref._id, rec); });
            m.set(sess._id, inner);
        });
        return m;
    }, [sessions]);

    // Roster rows — identical to the other tabs; falls back to the union of records mid-fetch.
    const students = useMemo(() => {
        if (!isTeacher) return [];
        if (Array.isArray(classMembers) && classMembers.length > 0) {
            return classMembers.filter(m => m && m._id)
                .map(m => ({ _id: m._id, name: m.name, student_id: m.student_id }))
                .sort(byLastName);
        }
        const map = {};
        sessions.forEach(sess => sess.records.forEach(rec => {
            if (rec.student_ref?._id) map[rec.student_ref._id] = rec.student_ref;
        }));
        return Object.values(map).sort(byLastName);
    }, [sessions, isTeacher, classMembers]);

    // ─── Record patch (status / homework / rating / attitude / comment) ───────
    // Optimistic local update, then persist; reload on failure to resync.
    const onPatchRecord = useCallback((sessionId, studentId, patch) => {
        setSessions(prev => prev.map(sess => {
            if (sess._id !== sessionId) return sess;
            const has = sess.records.some(rec => rec.student_ref?._id === studentId);
            const records = has
                ? sess.records.map(rec => rec.student_ref?._id === studentId ? { ...rec, ...patch } : rec)
                : [...sess.records, { student_ref: { _id: studentId }, ...patch }];
            return { ...sess, records };
        }));
        attendanceActionsRef.current.updateRecord(classIdRef.current, sessionId, studentId, patch)
            .then(res => { if (res?.status !== 'Success') { patchFailed(res); loadSessions(); } })
            .catch(() => { patchFailed(); loadSessions(); });
    }, [loadSessions]);

    const [regenerating, setRegenerating] = useState(false);
    const [uploadingProof, setUploadingProof] = useState(() => new Set());

    const onRegenerate = useCallback(async () => {
        if (!classIdRef.current) return;
        setRegenerating(true);
        try {
            const res = await attendanceActionsRef.current.regenerate(classIdRef.current);
            if (res?.status === 'Success') {
                message.success(`Đã tạo lại lịch (${res.message?.created ?? 0} buổi mới)`);
                await loadSessions();
            } else {
                message.error(res?.message || 'Tạo lại lịch thất bại');
            }
        } catch (e) { message.error('Tạo lại lịch thất bại'); }
        finally { setRegenerating(false); }
    }, [loadSessions]);

    const onDeleteSession = useCallback((sessionId) => {
        attendanceActionsRef.current.deleteSession(classIdRef.current, sessionId).then(res => {
            if (res?.status === 'Success') {
                setSessions(prev => prev.filter(s => s._id !== sessionId));
                setOpenSessionId(cur => cur === sessionId ? null : cur);
                message.success('Đã xóa buổi học');
            } else { message.error('Xóa thất bại'); }
        });
    }, []);

    const onNotifyAbsentees = useCallback((sessionId) => {
        attendanceActionsRef.current.notifyAbsentees(classIdRef.current, sessionId).then(res => {
            if (res?.status === 'Success') {
                const n = res.message?.notified ?? 0;
                message.success(n > 0 ? `Đã gửi thông báo cho ${n} học sinh vắng/muộn` : 'Không có học sinh vắng/muộn để thông báo');
            } else { message.error('Gửi thông báo thất bại'); }
        });
    }, []);

    const onUploadProof = useCallback((sessionId, file) => {
        setUploadingProof(prev => new Set(prev).add(sessionId));
        const hide = message.loading('Đang tải ảnh điểm danh...', 0);
        attendanceActionsRef.current.uploadProof(classIdRef.current, sessionId, file)
            .then(res => {
                if (res?.status === 'Success') {
                    setSessions(prev => prev.map(s => s._id === sessionId ? { ...s, proof: res.message.proof, held: true } : s));
                    message.success('Đã tải ảnh điểm danh');
                } else { message.error(typeof res?.message === 'string' ? res.message : 'Tải ảnh thất bại'); }
            })
            .catch(() => message.error('Tải ảnh thất bại'))
            .finally(() => {
                hide();
                setUploadingProof(prev => { const n = new Set(prev); n.delete(sessionId); return n; });
            });
        return false;
    }, []);

    const onDeleteProof = useCallback((sessionId) => {
        attendanceActionsRef.current.deleteProof(classIdRef.current, sessionId).then(res => {
            if (res?.status === 'Success') {
                setSessions(prev => prev.map(s => s._id === sessionId ? { ...s, proof: '', held: false } : s));
            } else { message.error('Xóa ảnh thất bại'); }
        });
    }, []);

    // ─── Cancel / un-cancel ────────────────────────────────────────────────────
    const [cancelTarget, setCancelTarget]   = useState(null);
    const [cancelReason, setCancelReason]    = useState('');
    const [cancelLoading, setCancelLoading]  = useState(false);

    const onCancelSession = useCallback(async (sessionId, reason) => {
        const trimmed = (reason || '').trim();
        if (!trimmed) { message.warning('Vui lòng nhập lý do hủy'); return; }
        setCancelLoading(true);
        try {
            const res = await attendanceActionsRef.current.cancelSession(classIdRef.current, sessionId, trimmed);
            if (res?.status === 'Success') {
                setSessions(prev => prev.map(s => s._id === sessionId ? { ...s, cancelled: true, cancel_reason: trimmed } : s));
                message.success('Đã hủy buổi học');
                setCancelTarget(null); setCancelReason('');
            } else { message.error(typeof res?.message === 'string' ? res.message : 'Hủy buổi học thất bại'); }
        } catch (e) { message.error('Hủy buổi học thất bại'); }
        finally { setCancelLoading(false); }
    }, []);

    const onUncancelSession = useCallback((sessionId) => {
        attendanceActionsRef.current.uncancelSession(classIdRef.current, sessionId).then(res => {
            if (res?.status === 'Success') {
                setSessions(prev => prev.map(s => s._id === sessionId ? { ...s, cancelled: false, cancel_reason: '' } : s));
                message.success('Đã khôi phục buổi học');
            } else { message.error('Khôi phục thất bại'); }
        }).catch(() => message.error('Khôi phục thất bại'));
    }, []);

    const onCheckIn = useCallback((sessionId) => {
        attendanceActionsRef.current.checkIn(classIdRef.current, sessionId).then(res => {
            if (res?.status === 'Success') {
                const { teacher_check_in_at, check_in_late } = res.message || {};
                setSessions(prev => prev.map(s => s._id === sessionId ? { ...s, teacher_check_in_at, check_in_late } : s));
                message.success(check_in_late ? 'Đã điểm danh — MUỘN (đã báo quản lý)' : 'Đã điểm danh đúng giờ');
            } else { message.error(typeof res?.message === 'string' ? res.message : 'Điểm danh thất bại'); }
        }).catch(() => message.error('Điểm danh thất bại'));
    }, []);

    // "Đánh dấu cả lớp có mặt" — set every still-unmarked, non-paused student to present in one PUT.
    const onMarkAllPresent = useCallback((sessionId) => {
        const inner = recordMaps.get(sessionId);
        const toSet = students.filter(stu => {
            const st = inner?.get(stu._id)?.status || '';
            return st !== 'paused' && st === '';
        }).map(stu => ({ student_ref: stu._id, status: 'present' }));
        if (!toSet.length) { message.info('Cả lớp đã được điểm danh'); return; }
        const ids = new Set(toSet.map(r => r.student_ref));
        setSessions(prev => prev.map(sess => {
            if (sess._id !== sessionId) return sess;
            const have = new Set(sess.records.map(r => r.student_ref?._id));
            const records = sess.records.map(rec => ids.has(rec.student_ref?._id) ? { ...rec, status: 'present' } : rec);
            toSet.forEach(r => { if (!have.has(r.student_ref)) records.push({ student_ref: { _id: r.student_ref }, status: 'present' }); });
            return { ...sess, records };
        }));
        attendanceActionsRef.current.bulkSetStatus(classIdRef.current, sessionId, toSet)
            .then(res => { if (res?.status !== 'Success') { patchFailed(res); loadSessions(); } })
            .catch(() => { patchFailed(); loadSessions(); });
    }, [students, recordMaps, loadSessions]);

    // ─── Per-session tallies ────────────────────────────────────────────────────
    const sessStats = useCallback((sess) => {
        const inner = recordMaps.get(sess._id);
        let present = 0, absent = 0, late = 0, excused = 0, paused = 0, total = 0, unmarked = 0;
        students.forEach(stu => {
            const st = inner?.get(stu._id)?.status || '';
            if (st === 'paused') { paused += 1; return; }
            total += 1;
            if (st === 'present') present += 1;
            else if (st === 'absent') absent += 1;
            else if (st === 'late') late += 1;
            else if (st === 'excused') excused += 1;
            else unmarked += 1;
        });
        return { present, absent, late, excused, paused, total, unmarked };
    }, [recordMaps, students]);

    // ─── Create session ───────────────────────────────────────────────────────
    async function handleCreateSession() {
        let values;
        try { values = await form.validateFields(); } catch { return; }
        setCreateLoading(true);
        try {
            const dateStr = values.date.format('YYYY-MM-DD');
            const res = await attendanceActionsRef.current.createSession(classIdRef.current, values.lesson_name, dateStr, values.description);
            if (res?.status === 'Success') {
                message.success('Đã tạo buổi học');
                setCreateVisible(false); form.resetFields();
            } else { message.error(typeof res?.message === 'string' ? res.message : 'Tạo buổi học thất bại'); }
        } catch (e) { message.error('Có lỗi xảy ra khi tạo buổi học'); }
        finally { setCreateLoading(false); await loadSessions(); }
    }

    // ─── Edit session info (lesson name / description) ────────────────────────
    async function onSaveSessionInfo() {
        const lesson_name = editLessonName.trim();
        if (!lesson_name) { message.warning('Tên buổi học không được để trống'); return; }
        setEditInfoLoading(true);
        try {
            const description = editDescription.trim();
            const res = await attendanceActionsRef.current.updateSessionInfo(classIdRef.current, editInfoTarget, { lesson_name, description });
            if (res?.status === 'Success') {
                setSessions(prev => prev.map(s => s._id === editInfoTarget ? { ...s, lesson_name, description } : s));
                message.success('Đã lưu');
                setEditInfoTarget(null);
            } else { message.error(typeof res?.message === 'string' ? res.message : 'Lưu thất bại'); }
        } catch (e) { message.error('Có lỗi xảy ra'); }
        finally { setEditInfoLoading(false); }
    }

    const sessionHandlers = {
        onCheckIn, onMarkAllPresent, onUploadProof, onDeleteProof,
        onNotifyAbsentees, onUncancelSession, onDeleteSession,
        onOpenCancel: (id) => { setCancelTarget(id); setCancelReason(''); },
        onOpenEditInfo: (sess) => {
            setEditInfoTarget(sess._id);
            setEditLessonName(sess.lesson_name);
            setEditDescription(sess.description || '');
        },
    };

    // Shared ⋯ menu for a session (card kebab + detail header).
    const sessionMenu = (sess) => (
        <Menu>
            <Menu.Item key="editinfo" icon={<EditOutlined />} onClick={() => sessionHandlers.onOpenEditInfo(sess)}>Sửa tên / nội dung buổi</Menu.Item>
            {!sess.cancelled && !sess.teacher_check_in_at &&
                <Menu.Item key="checkin" icon={<LoginOutlined />} onClick={() => sessionHandlers.onCheckIn(sess._id)}>Điểm danh giáo viên</Menu.Item>}
            {sess.teacher_check_in_at &&
                <Menu.Item key="checked" disabled icon={<CheckCircleOutlined />}>GV {dayjs(sess.teacher_check_in_at).format('HH:mm')}{sess.check_in_late ? ' (muộn)' : ''}</Menu.Item>}
            {!sess.cancelled &&
                <Menu.Item key="all" icon={<CheckOutlined />} onClick={() => sessionHandlers.onMarkAllPresent(sess._id)}>Đánh dấu cả lớp có mặt</Menu.Item>}
            {!sess.cancelled &&
                <Menu.Item key="proof" icon={<CameraOutlined />} disabled={uploadingProof.has(sess._id)}>
                    <Upload showUploadList={false} accept="image/*" disabled={uploadingProof.has(sess._id)}
                        beforeUpload={file => sessionHandlers.onUploadProof(sess._id, file)}>
                        {sess.proof ? 'Đổi ảnh điểm danh' : 'Tải ảnh điểm danh'}
                    </Upload>
                </Menu.Item>}
            {sess.proof &&
                <Menu.Item key="delproof" icon={<DeleteOutlined />}
                    onClick={() => Modal.confirm({ title: 'Xóa ảnh điểm danh?', okText: 'Xóa', cancelText: 'Hủy', okButtonProps: { danger: true }, onOk: () => sessionHandlers.onDeleteProof(sess._id) })}>Xóa ảnh điểm danh</Menu.Item>}
            <Menu.Item key="notify" icon={<BellOutlined />}
                onClick={() => Modal.confirm({ title: 'Gửi thông báo cho học sinh vắng/muộn buổi này?', okText: 'Gửi', cancelText: 'Hủy', onOk: () => sessionHandlers.onNotifyAbsentees(sess._id) })}>Gửi thông báo vắng/muộn</Menu.Item>
            <Menu.Divider />
            {sess.cancelled
                ? <Menu.Item key="uncancel" icon={<RollbackOutlined />}
                    onClick={() => Modal.confirm({ title: 'Khôi phục buổi học này?', okText: 'Khôi phục', cancelText: 'Hủy', onOk: () => sessionHandlers.onUncancelSession(sess._id) })}>Khôi phục buổi học</Menu.Item>
                : <Menu.Item key="cancel" icon={<StopOutlined />} onClick={() => sessionHandlers.onOpenCancel(sess._id)}>Hủy buổi học</Menu.Item>}
            <Menu.Item key="delete" danger icon={<DeleteOutlined />}
                onClick={() => Modal.confirm({ title: 'Xóa buổi học này?', okText: 'Xóa', cancelText: 'Hủy', okButtonProps: { danger: true }, onOk: () => sessionHandlers.onDeleteSession(sess._id) })}>Xóa buổi học</Menu.Item>
        </Menu>
    );

    // Modals reused by both views.
    const modals = (
        <>
            <Modal title="Tạo buổi học mới" visible={createVisible}
                onCancel={() => { setCreateVisible(false); form.resetFields(); }}
                onOk={handleCreateSession} confirmLoading={createLoading} okText="Tạo" cancelText="Hủy">
                <Form form={form} layout="vertical">
                    <Form.Item name="lesson_name" label="Tên buổi học" rules={[{ required: true, message: 'Vui lòng nhập tên buổi học' }]}>
                        <Input placeholder="VD: Buổi 1, Tuần 1..." />
                    </Form.Item>
                    <Form.Item name="date" label="Ngày" rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <Form.Item name="description" label="Nội dung buổi học (tùy chọn)">
                        <Input.TextArea rows={3} placeholder="VD: Ôn tập Unit 3, luyện nói..." />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal title="Sửa tên / nội dung buổi" visible={editInfoTarget !== null}
                onCancel={() => setEditInfoTarget(null)}
                onOk={onSaveSessionInfo} confirmLoading={editInfoLoading} okText="Lưu" cancelText="Hủy">
                <div style={{ marginBottom: 8 }}>Tên buổi học <span style={{ color: 'var(--bad)' }}>*</span></div>
                <Input value={editLessonName} onChange={e => setEditLessonName(e.target.value)}
                    placeholder="VD: Buổi 1, Tuần 1..." style={{ marginBottom: 12 }} />
                <div style={{ marginBottom: 8 }}>Nội dung buổi học</div>
                <Input.TextArea rows={3} value={editDescription} onChange={e => setEditDescription(e.target.value)}
                    placeholder="VD: Ôn tập Unit 3, luyện nói..." />
            </Modal>
            <Modal title="Hủy buổi học" visible={cancelTarget !== null} maskClosable={false}
                onCancel={() => { setCancelTarget(null); setCancelReason(''); }}
                onOk={() => onCancelSession(cancelTarget, cancelReason)} confirmLoading={cancelLoading}
                okText="Hủy buổi" cancelText="Đóng" okButtonProps={{ danger: true, disabled: !cancelReason.trim() }}>
                <div style={{ marginBottom: 8 }}>Lý do hủy <span style={{ color: 'var(--bad)' }}>*</span></div>
                <Input.TextArea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                    placeholder="VD: Giáo viên nghỉ ốm, trùng lịch nghỉ lễ..." />
            </Modal>
        </>
    );

    // ─── Student personal view (unchanged: read-only sessions list) ───────────
    const myMongoId = useMemo(() => {
        if (isTeacher) return null;
        const myCode = userData?.student_id;
        for (const sess of sessions) for (const rec of sess.records)
            if (rec.student_ref?.student_id === myCode) return rec.student_ref._id;
        return null;
    }, [sessions, isTeacher, userData?.student_id]);

    const personalRows = useMemo(() => {
        if (isTeacher) return [];
        return [...weekSessions].sort((a, b) => new Date(b.date) - new Date(a.date)).map(sess => {
            const rec = recordMaps.get(sess._id)?.get(myMongoId);
            return { key: sess._id, lesson_name: sess.lesson_name, date: sess.date, status: rec?.status || '' };
        });
    }, [weekSessions, isTeacher, myMongoId, recordMaps]);

    const personalAttended = useMemo(() => {
        if (isTeacher) return 0;
        return sessions.reduce((n, sess) => {
            const rec = recordMaps.get(sess._id)?.get(myMongoId);
            return n + (rec && (rec.status === 'present' || rec.status === 'late') ? 1 : 0);
        }, 0);
    }, [sessions, isTeacher, myMongoId, recordMaps]);

    const personalCols = useMemo(() => [
        { title: 'Buổi học', dataIndex: 'lesson_name', key: 'lesson_name', width: 130, render: t => <span style={{ fontWeight: 600 }}>{t}</span> },
        { title: 'Ngày', dataIndex: 'date', key: 'date', width: 120, render: d => dayjs(d).format('DD/MM/YYYY') },
        { title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 120, align: 'center', render: s => <StatusTag status={s} /> },
    ], []);

    if (!isTeacher) {
        const total = sessions.length;
        const pct = total > 0 ? Math.round((personalAttended / total) * 100) : 0;
        const tone = pct >= 80 ? 'ok' : pct >= 60 ? 'warn' : 'bad';
        return (
            <div className="att2-page">
                <div className="att2-head">
                    <div className="att2-head-l"><h1 className="att2-title">Điểm danh của tôi</h1></div>
                    {total > 0 && <span className={`att2-rate-n tone-${tone}`} style={{ fontSize: 15 }}>{personalAttended}/{total} buổi ({pct}%)</span>}
                </div>
                <div className="att2-weeknav-wrap">{weekControls}</div>
                <Table columns={personalCols} dataSource={personalRows} loading={loading} pagination={false}
                    bordered size="middle" locale={{ emptyText: 'Tuần này chưa có buổi học' }} />
            </div>
        );
    }

    const className = classWrapper.curClass?.class_name || 'Lớp';
    const isTrial = !!classWrapper.curClass?.is_trial;
    // rejectIfArchived guards every write route on this screen, so without this the controls stay
    // live on a read-only class and each click earns a 403. The picker already tags these "Chỉ đọc".
    const isArchived = !!classWrapper.curClass?.archived;
    const openSession = openSessionId ? sessions.find(s => s._id === openSessionId) : null;

    // ─── Detail view: one session's roster ────────────────────────────────────
    if (openSession) {
        const st = sessStats(openSession);
        const start = openSession.date ? dayjs(openSession.date) : null;
        const dayStr = start ? `${dayLabel(start)} · ${start.format('DD/MM/YYYY')} · ${start.format('HH:mm')}` : '';
        const q = normalizeVietnamese(search);
        const rows = students.filter(stu =>
            !q.trim() || normalizeVietnamese(stu.name).includes(q) || (stu.student_id || '').toLowerCase().includes(q));
        const editable = !openSession.cancelled && !isArchived;
        // Lớp học thử: the teacher still checks in (that's what records their teaching hours), but
        // nobody marks the trial student. The proof photo is hidden too, not just for tidiness —
        // proof sets `held`, and `held` is what makes a session count against a student's lesson
        // quota, so proof on a trial session would burn quota a trial student doesn't have (and, for
        // a converted student still listed in their old trial class, real quota).
        const markable = editable && !isTrial;

        return (
            <div className="att2-page">
                <button className="att2-back" onClick={() => { setOpenSessionId(null); setSearch(''); }}>
                    <ArrowLeftOutlined /> Tất cả buổi học
                </button>
                <div className="att2-detail-head">
                    <div className="att2-detail-l">
                        <div className="att2-sh-day">{dayStr}</div>
                        <h1 className="att2-title">{openSession.lesson_name}</h1>
                        {openSession.description && <div className="att2-sub">{openSession.description}</div>}
                        <div className="att2-sh-tags" style={{ marginTop: 8 }}>
                            {openSession.missed && <span className="att2-tag t-bad">Bỏ lỡ</span>}
                            {(openSession.held || openSession.proof) && <span className="att2-tag t-ok">Đã học</span>}
                            {openSession.cancelled && <Tooltip title={openSession.cancel_reason || 'Đã hủy'}><span className="att2-tag t-warn">Đã hủy</span></Tooltip>}
                            {openSession.teacher_check_in_at &&
                                <span className={openSession.check_in_late ? 'att2-tag t-bad' : 'att2-tag t-ok'}>GV {dayjs(openSession.teacher_check_in_at).format('HH:mm')}</span>}
                            {openSession.punctuality_confirmed && <span className="att2-tag t-ok">QL xác nhận</span>}
                        </div>
                    </div>
                    <div className="att2-detail-actions">
                        {openSession.proof &&
                            <div className="att2-proof-lg"><Image src={`/api/photos/${openSession.proof}`} width={56} height={56} style={{ objectFit: 'cover' }} /></div>}
                        {editable && !openSession.teacher_check_in_at &&
                            <Button type="primary" icon={<LoginOutlined />} onClick={() => onCheckIn(openSession._id)}>Điểm danh GV</Button>}
                        {markable &&
                            <Button type={openSession.teacher_check_in_at ? 'primary' : 'default'} icon={<CheckOutlined />} onClick={() => onMarkAllPresent(openSession._id)}>Cả lớp có mặt</Button>}
                        {markable &&
                            <Upload showUploadList={false} accept="image/*" disabled={uploadingProof.has(openSession._id)}
                                beforeUpload={file => onUploadProof(openSession._id, file)}>
                                <Button icon={<CameraOutlined />}>{openSession.proof ? 'Đổi ảnh' : 'Ảnh'}</Button>
                            </Upload>}
                        {!isArchived &&
                            <Dropdown overlay={sessionMenu(openSession)} trigger={['click']} placement="bottomRight">
                                <Button icon={<EllipsisOutlined />} aria-label="Thao tác buổi học" />
                            </Dropdown>}
                    </div>
                </div>

                {isTrial ? (
                    <div className="stat-strip">
                        <span className="stat"><span className="l">Sĩ số</span><span className="v">{st.total}</span></span>
                    </div>
                ) : (
                <div className="stat-strip">
                    <span className="stat"><span className="l">Sĩ số</span><span className="v">{st.total}</span></span>
                    <span className="stat"><span className="l">Có mặt</span><span className="v tone-ok">{st.present + st.late}</span></span>
                    <span className="stat"><span className="l">Vắng</span><span className="v tone-bad">{st.absent}</span></span>
                    <span className="stat"><span className="l">Muộn</span><span className="v tone-warn">{st.late}</span></span>
                    <span className="stat"><span className="l">Có phép</span><span className="v">{st.excused}</span></span>
                    {st.paused > 0 && <span className="stat"><span className="l">Bảo lưu</span><span className="v">{st.paused}</span></span>}
                    {st.unmarked > 0 && <span className="stat"><span className="l">Chưa ĐD</span><span className="v">{st.unmarked}</span></span>}
                </div>
                )}

                {isTrial ? (
                    // No roster at all — a trial class exists to record that the teacher taught it, and
                    // the trial student is never marked. Rendering six disabled controls per row would
                    // only invite someone to wonder why they're dead.
                    <div className="att2-empty">
                        Lớp học thử không điểm danh học sinh. Giáo viên chỉ cần bấm “Điểm danh GV” để ghi nhận giờ dạy.
                    </div>
                ) : (
                <>
                <Input className="att2-search" prefix={<SearchOutlined style={{ color: 'var(--ink-4)' }} />}
                    placeholder="Tìm học sinh..." value={search} onChange={e => setSearch(e.target.value)} allowClear
                    style={{ maxWidth: 260, marginBottom: 12 }} />

                <div className="att2-rtable-wrap">
                    <table className="att2-rtable">
                        <thead>
                            <tr>
                                <th className="att2-rt-stu">Học sinh</th>
                                <th>Trạng thái</th>
                                <th>BTVN</th>
                                <th>Kết quả</th>
                                <th>Ý thức</th>
                                <th>Nhận xét</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(stu => {
                                const rec = recordMaps.get(openSession._id)?.get(stu._id) || {};
                                const rk = `${openSession._id}:${stu._id}`;
                                return (
                                    <tr key={stu._id}>
                                        <td className="att2-rt-stu">
                                            <div className="att2-stu">
                                                <span className="att2-av">{initials(stu.name)}</span>
                                                <span style={{ minWidth: 0 }}>
                                                    <span className="att2-stu-n">{stu.name}</span>
                                                    <span className="att2-stu-c">{stu.student_id}</span>
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <StatusSelect status={rec.status || ''} disabled={!editable}
                                                onPick={s => onPatchRecord(openSession._id, stu._id, { status: s })} />
                                        </td>
                                        <td>
                                            {editable ? (
                                                <Select allowClear size="small" style={{ width: 150 }} placeholder="Chưa chấm"
                                                    value={rec.homework || undefined}
                                                    onChange={v => onPatchRecord(openSession._id, stu._id, { homework: v || '' })}
                                                    options={HOMEWORK_LEVELS.map(l => ({ value: l.code, label: l.label }))} />
                                            ) : <HomeworkTag code={rec.homework} />}
                                        </td>
                                        <td>
                                            <Rate count={10} disabled={!editable} value={rec.rating || 0} style={{ fontSize: 14 }}
                                                onChange={v => onPatchRecord(openSession._id, stu._id, { rating: v })} />
                                        </td>
                                        <td>
                                            <Input key={`${rk}:att`} size="small" defaultValue={rec.attitude || ''} disabled={!editable}
                                                placeholder="Ý thức..."
                                                onBlur={e => { const v = e.target.value; if (v !== (rec.attitude || '')) onPatchRecord(openSession._id, stu._id, { attitude: v }); }} />
                                        </td>
                                        <td>
                                            <Input key={`${rk}:cmt`} size="small" defaultValue={rec.comment || ''} disabled={!editable}
                                                placeholder="Nhận xét..."
                                                onBlur={e => { const v = e.target.value; if (v !== (rec.comment || '')) onPatchRecord(openSession._id, stu._id, { comment: v }); }} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                </>
                )}
                {modals}
            </div>
        );
    }

    // ─── List view: session cards ─────────────────────────────────────────────
    const now = dayjs();
    const displaySessions = attendanceView === 'today' ? todaySessions : weekSessions;
    const displayLabel = attendanceView === 'today'
        ? `Hôm nay · ${todaySessions.length} buổi`
        : `${weekLabel} · ${weekCount} buổi`;
    const emptyText = sessions.length === 0
        ? 'Chưa có buổi học nào. Hãy tạo buổi học đầu tiên!'
        : attendanceView === 'today'
            ? 'Hôm nay chưa có buổi học'
            : 'Tuần này chưa có buổi học';
    return (
        <div className="att2-page">
            <div className="att2-crumb">Lớp {className} / Điểm danh</div>
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Điểm danh{isArchived && <span className="tag t-neutral att2-title-tag">Chỉ đọc</span>}</h1>
                    <div className="att2-sub">Lớp {className} · {students.length} học sinh · {sessions.length} buổi học</div>
                </div>
                <div className="att2-tools">
                    <div className="seg" role="tablist" aria-label="Chế độ xem điểm danh">
                        <button type="button" className={attendanceView === 'week' ? 'on' : ''} onClick={() => setAttendanceView('week')}>Tuần</button>
                        <button type="button" className={attendanceView === 'today' ? 'on' : ''} onClick={() => setAttendanceView('today')}>
                            Hôm nay <span className="seg-n">{todaySessions.length}</span>
                        </button>
                    </div>
                    <div className="att2-weeknav">
                        <button className="att2-weeknav-btn" disabled={!weekHasPast} onClick={weekPrev} aria-label="Tuần trước"><LeftOutlined /></button>
                        <button className="att2-weeknav-label" onClick={weekToday}>Tuần này</button>
                        <button className="att2-weeknav-btn" disabled={!weekHasFuture} onClick={weekNext} aria-label="Tuần sau"><RightOutlined /></button>
                    </div>
                    {!isArchived && <>
                        <Tooltip title="Tạo lại các buổi học từ lịch học của lớp (giữ buổi đã học, dời buổi bỏ lỡ)">
                            <Button icon={<ReloadOutlined />} loading={regenerating} onClick={onRegenerate}>Tạo lại lịch</Button>
                        </Tooltip>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>Thêm buổi</Button>
                    </>}
                </div>
            </div>

            <div className="att2-weeklabel att2-weeklabel-top">{displayLabel}</div>

            {displaySessions.length === 0 ? (
                <div className="att2-empty">
                    <div>{emptyText}</div>
                    {attendanceView === 'today' && sessions.length > 0 &&
                        <Button size="small" style={{ marginTop: 12 }} onClick={() => setAttendanceView('week')}>Xem tuần</Button>}
                </div>
            ) : (
                <div className="att2-cards">
                    {displaySessions.map(sess => {
                        const start = sess.date ? dayjs(sess.date) : null;
                        const active = !!start && now.isSame(start, 'day') && now.isAfter(start) && now.isBefore(start.add(3, 'hour'));
                        const stats = sessStats(sess);
                        const dayStr = start ? `${dayLabel(start)} · ${start.format('DD/MM')}` : '';
                        const timeStr = start ? start.format('HH:mm') : '';
                        const editable = !sess.cancelled && !isArchived;
                        const markable = editable && !isTrial;
                        const emphasized = active || (attendanceView === 'today' && sess._id === nearestTodayId);
                        return (
                            <div key={sess._id} className={'att2-card' + (emphasized ? ' is-current' : '')} onClick={() => setOpenSessionId(sess._id)}
                                role="button" tabIndex={0}
                                onKeyDown={e => { if (e.key === 'Enter') setOpenSessionId(sess._id); }}>
                                {!isArchived &&
                                    <Dropdown overlay={sessionMenu(sess)} trigger={['click']} placement="bottomRight">
                                        <button className="att2-kebab" aria-label="Thao tác buổi học" onClick={e => e.stopPropagation()}>
                                            <EllipsisOutlined />
                                        </button>
                                    </Dropdown>}
                                <div className="att2-sh-day">{dayStr}{timeStr && ` · ${timeStr}`}</div>
                                <div className="att2-sh-meta">
                                    {sess.cancelled ? 'Đã hủy'
                                        : stats.unmarked === stats.total ? 'Chưa điểm danh'
                                        : `${stats.present + stats.late}/${stats.total} có mặt`}
                                    {!sess.cancelled && stats.unmarked > 0 && stats.unmarked < stats.total &&
                                        <span className="att2-sh-warn"> · còn {stats.unmarked} chưa ĐD</span>}
                                </div>
                                <div className="att2-sh-name">{sess.lesson_name}</div>
                                {sess.description && <div className="att2-sh-desc">{sess.description}</div>}
                                <div className="att2-sh-tags">
                                    {active && <span className="att2-tag t-info">Đang diễn ra</span>}
                                    {sess.missed && <span className="att2-tag t-bad">Bỏ lỡ</span>}
                                    {(sess.held || sess.proof) && <span className="att2-tag t-ok">Đã học</span>}
                                    {sess.cancelled && <span className="att2-tag t-warn">Đã hủy</span>}
                                    {sess.teacher_check_in_at &&
                                        <span className={sess.check_in_late ? 'att2-tag t-bad' : 'att2-tag t-ok'}>GV {dayjs(sess.teacher_check_in_at).format('HH:mm')}</span>}
                                    {sess.proof &&
                                        <span className="att2-sh-proof"><Image src={`/api/photos/${sess.proof}`} width={18} height={18} style={{ objectFit: 'cover' }} preview={false} /></span>}
                                </div>
                                <div className="att2-card-actions" onClick={e => e.stopPropagation()}>
                                    {editable && !sess.teacher_check_in_at &&
                                        <Button size="small" type={active ? 'primary' : 'default'} icon={<LoginOutlined />} onClick={() => onCheckIn(sess._id)}>Điểm danh GV</Button>}
                                    {markable &&
                                        <Button size="small" icon={<CheckOutlined />} onClick={() => onMarkAllPresent(sess._id)}>Cả lớp có mặt</Button>}
                                    {markable &&
                                        <Upload showUploadList={false} accept="image/*" disabled={uploadingProof.has(sess._id)}
                                            beforeUpload={file => onUploadProof(sess._id, file)}>
                                            <Button size="small" icon={<CameraOutlined />} loading={uploadingProof.has(sess._id)}>{sess.proof ? 'Đổi ảnh' : 'Ảnh'}</Button>
                                        </Upload>}
                                    <Button size="small" type="link" onClick={() => setOpenSessionId(sess._id)}>Mở danh sách</Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {modals}
        </div>
    );
}
